import withI18n, { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Alert, InfoTooltip } from "@impero-com/ui";
import { Row, Col, Checkbox } from "antd";

import { AssignableRoles, Role } from "api/generated/model";
import { FormCreate, FormRootComponentProps } from "utils/form";

import { useFeatureFlag } from "../../utils/hooks/useFeatureFlag";

import * as theme from "./theme.css";

interface RoleAssignmentFormProps extends FormRootComponentProps<object> {
    assignableRoles: AssignableRoles;
    roles?: Array<Role>;
    onChangeRole(role: Role, selected: boolean): void;
}

function RoleAssignmentForm({
    assignableRoles,
    roles,
    onChangeRole,
}: RoleAssignmentFormProps) {
    const controlTestingEnabled = useFeatureFlag("controlTesting");
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const mcpEnabled = useFeatureFlag("mcp");
    const renderCheckbox = (role: Role) => (
        <Col sm={8} span={24}>
            <div className={theme.roleContainer}>
                <Checkbox
                    id={role}
                    checked={roles ? roles.indexOf(role) >= 0 : undefined}
                    disabled={
                        !(
                            assignableRoles.type === "Allowed" &&
                            assignableRoles.roles.includes(role)
                        )
                    }
                    onChange={(e) => onChangeRole(role, e.target.checked)}
                >
                    {getLocalizedRoleName(role)}
                </Checkbox>
                <span className={theme.roleTooltipContainer}>
                    <InfoTooltip text={getLocalizedRoleDescription(role)} />
                </span>
            </div>
        </Col>
    );

    return (
        <>
            {assignableRoles.type === "Forbidden" ? (
                <Alert
                    type="warning"
                    message={<strong>{t("CreateUser_NoPermission")}</strong>}
                />
            ) : (
                <></>
            )}
            <Row gutter={[6, 6]} className={theme.roleRow}>
                {renderCheckbox("OrganizationAdmin")}
                {renderCheckbox("OrganizationUserAdmin")}
                {renderCheckbox("OrganizationUserManager")}
                {renderCheckbox("OrganizationTagAdmin")}
                {renderCheckbox("OrganizationLogAdmin")}
                {renderCheckbox("OrganizationControlManager")}
                {controlEntitiesEnabled &&
                    renderCheckbox("OrganizationControlEntityAdmin")}
                {renderCheckbox("OrganizationReportManager")}
                {renderCheckbox("OrganizationRiskDirectoryManager")}
                {renderCheckbox("OrganizationApiAdmin")}
                {mcpEnabled && renderCheckbox("OrganizationMcpUser")}
                {controlTestingEnabled &&
                    renderCheckbox("OrganizationTestingManager")}
            </Row>
        </>
    );
}

export function getLocalizedRoleName(role: Role): string {
    switch (role) {
        case "OrganizationAdmin":
            return t("User_Roles_OrganizationAdmin");
        case "OrganizationUserAdmin":
            return t("User_Roles_OrganizationUserAdmin");
        case "OrganizationUserManager":
            return t("User_Roles_OrganizationUserManager");
        case "OrganizationTagAdmin":
            return t("User_Roles_OrganizationTagAdmin");
        case "OrganizationLogAdmin":
            return t("User_Roles_OrganizationLogAdmin");
        case "OrganizationControlManager":
            return t("User_Roles_OrganizationControlManager");
        case "OrganizationControlEntityAdmin":
            return t("User_Roles_OrganizationControlEntityAdmin");
        case "OrganizationReportManager":
            return t("User_Roles_OrganizationReportManager");
        case "OrganizationRiskDirectoryManager":
            return t("User_Roles_OrganizationRiskDirectoryManager");
        case "OrganizationApiAdmin":
            return t("User_Roles_OrganizationApiAdmin");
        case "OrganizationLocalizationAdmin":
            // Should not happen in prod,
            // but we can't throw new Error here because that would break staging and local dev setup
            return "Localization administrator";
        case "OrganizationTestingManager":
            return t("User_Roles_OrganizationTestingManager");
        case "OrganizationMcpUser":
            return t("User_Roles_OrganizationMcpUser");
        default:
            assertNever(role);
    }
}

export function getLocalizedRoleDescription(role: Role): string {
    switch (role) {
        case "OrganizationAdmin":
            return t("User_Roles_Description_OrganizationAdmin");
        case "OrganizationUserAdmin":
            return t("User_Roles_Description_OrganizationUserAdmin");
        case "OrganizationUserManager":
            return t("User_Roles_Description_OrganizationUserManager");
        case "OrganizationTagAdmin":
            return t("User_Roles_Description_OrganizationTagAdmin");
        case "OrganizationLogAdmin":
            return t("User_Roles_Description_OrganizationLogAdmin");
        case "OrganizationControlManager":
            return t("User_Roles_Description_OrganizationControlManager");
        case "OrganizationControlEntityAdmin":
            return t("User_Roles_Description_OrganizationControlEntityAdmin");
        case "OrganizationReportManager":
            return t("User_Roles_Description_OrganizationReportManager");
        case "OrganizationRiskDirectoryManager":
            return t("User_Roles_Description_OrganizationRiskDirectoryManager");
        case "OrganizationApiAdmin":
            return t("User_Roles_Description_OrganizationApiAdmin");
        case "OrganizationLocalizationAdmin":
            // Should not happen in prod,
            // but we can't throw new Error here because that would break staging and local dev setup
            return "Manages Localization";
        case "OrganizationTestingManager":
            return t("User_Roles_Description_OrganizationTestingManager");
        case "OrganizationMcpUser":
            return t("User_Roles_Description_OrganizationMcpUser");
        default:
            assertNever(role);
    }
}

export default FormCreate<object, RoleAssignmentFormProps>()(
    withI18n<RoleAssignmentFormProps>(RoleAssignmentForm)
);
